import { useEffect, useRef, useState } from "react";
import { hasFinePointer, prefersReducedMotion } from "../theme";

/* ═══════════════════════════════════════════════════════════════════════
   crAIg has a face.

   With the rest of the interface deliberately quiet, this carries most of
   the personality — and it is the one piece of crAIg's own branding that
   appears on every screen, so it keeps HIS colour rather than the
   practice's.

   The behaviour is small and specific: eyes that follow the cursor,
   irregular blinking, a nod as he starts speaking, a distracted upward
   glance while thinking. It is the difference between a form with a
   chatbot bolted on and something a nervous person feels able to talk to.
   ═══════════════════════════════════════════════════════════════════════ */

/**
 * One shared pointer position for every face on screen. Separate listeners
 * would let two faces look in slightly different directions on the same
 * frame, which reads as broken rather than alive.
 */
const pointer = { x: 0.5, y: 0.5, subs: new Set<() => void>() };

if (typeof window !== "undefined") {
  window.addEventListener(
    "mousemove",
    (e) => {
      pointer.x = e.clientX / window.innerWidth;
      pointer.y = e.clientY / window.innerHeight;
      pointer.subs.forEach((fn) => fn());
    },
    { passive: true },
  );
}

export function CraigFace({
  size = 40,
  thinking = false,
  speaking = false,
  brand = false,
}: {
  size?: number;
  thinking?: boolean;
  speaking?: boolean;
  /**
   * Wear the business's colour instead of crAIg's.
   *
   * Off by default, and that default is the considered one: a consistent
   * face is part of what makes him a known quantity to somebody who has
   * met him at another business. But the customer came to deal with THIS
   * business, and a plumber who wants their own colour on their own page
   * is not wrong. Their choice, ours to default.
   */
  brand?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [gaze, setGaze] = useState({ x: 0, y: 0 });
  const [blink, setBlink] = useState(false);
  const [nod, setNod] = useState(false);
  const [muse, setMuse] = useState(0);

  const still = prefersReducedMotion();

  useEffect(() => {
    if (still || !hasFinePointer()) return;
    const update = () => {
      const el = box.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const cx = (r.left + r.width / 2) / window.innerWidth;
      const cy = (r.top + r.height / 2) / window.innerHeight;
      const clamp = (v: number) => Math.max(-1, Math.min(1, v));
      // Clamped so he glances rather than stares.
      setGaze({ x: clamp((pointer.x - cx) * 3), y: clamp((pointer.y - cy) * 3) });
    };
    pointer.subs.add(update);
    update();
    return () => void pointer.subs.delete(update);
  }, [still]);

  // Irregular blinking. A fixed interval looks robotic; the randomness is
  // what sells it.
  useEffect(() => {
    if (still) return;
    let timer: ReturnType<typeof setTimeout>;
    const loop = () => {
      timer = setTimeout(
        () => {
          setBlink(true);
          setTimeout(() => setBlink(false), 120);
          loop();
        },
        2400 + Math.random() * 4200,
      );
    };
    loop();
    return () => clearTimeout(timer);
  }, [still]);

  useEffect(() => {
    if (still || !speaking) return;
    setNod(true);
    const t = setTimeout(() => setNod(false), 400);
    return () => clearTimeout(t);
  }, [speaking, still]);

  useEffect(() => {
    if (still || !thinking) {
      setMuse(0);
      return;
    }
    let i = 0;
    const id = setInterval(() => {
      i = (i + 1) % 4;
      setMuse(i);
    }, 620);
    return () => clearInterval(id);
  }, [thinking, still]);

  const drift = [
    { x: -0.7, y: -0.6 },
    { x: 0.5, y: -0.7 },
    { x: 0.8, y: -0.2 },
    { x: -0.3, y: -0.5 },
  ][muse]!;

  const gx = thinking ? drift.x : gaze.x;
  const gy = thinking ? drift.y : gaze.y;
  const px = gx * 1.4;
  const py = gy * 1.1;

  return (
    <div
      ref={box}
      className="shrink-0"
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.28,
        // His mark by default. Every fill below reads --craig, so scoping
        // the variable here re-colours the whole face in one line rather
        // than threading a colour through six shapes.
        ...(brand ? ({ ["--craig"]: "var(--accent)" } as React.CSSProperties) : {}),
        background: "var(--craig)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transform: nod ? "translateY(-2px)" : "none",
        transition: "transform .2s cubic-bezier(.2,.9,.3,1)",
      }}
    >
      <svg viewBox="0 0 32 32" width={size * 0.64} height={size * 0.64}>
        <rect x="3" y="6" width="26" height="20" rx="3.5" fill="#fff" opacity=".96" />

        <g>
          <circle cx={11 + px} cy={15 + py} r="2.3" fill="var(--craig)" style={eye} />
          <circle cx={21 + px} cy={15 + py} r="2.3" fill="var(--craig)" style={eye} />
          {/* Lids drop from above rather than the eye vanishing */}
          <rect x="7.5" y="11" width="7" height={blink ? 7 : 0} fill="#fff" style={lid} />
          <rect x="17.5" y="11" width="7" height={blink ? 7 : 0} fill="#fff" style={lid} />
        </g>

        {speaking ? (
          <ellipse cx="16" cy="21" rx="2.4" ry="1.7" fill="var(--craig)" opacity=".85" />
        ) : thinking ? (
          <path d="M12.5 21 L19.5 21" stroke="var(--craig)" strokeWidth="1.6" strokeLinecap="round" />
        ) : (
          <path
            d="M11.5 20 Q16 23.5 20.5 20"
            stroke="var(--craig)"
            strokeWidth="1.6"
            fill="none"
            strokeLinecap="round"
          />
        )}
      </svg>
    </div>
  );
}

const eye: React.CSSProperties = { transition: "cx .25s ease-out, cy .25s ease-out" };
const lid: React.CSSProperties = { transition: "height .07s linear" };
